import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Collapsible from '@radix-ui/react-collapsible';
import * as Select from '@radix-ui/react-select';
import { observer } from 'mobx-react-lite';
import { usePersonLifeEventsStore, useFamilyStore } from '@/hooks/useStores';
import { eventTypesApi } from '@/api';
import type { CreateLifeEventRequest, UpdateLifeEventRequest } from 'we-tree-api-client/v2';
import type { LifeEventType } from '@/stores/models/LifeEvent';
import { formatTemporal } from '@/utils/dateFormatting';
import type { DatePrecision } from '@/utils/dateFormatting';
import DateFieldWithPrecision from '@/components/shared/DateFieldWithPrecision';
import CollapsibleChevron from '@/components/shared/CollapsibleChevron';
import Close from '@/icons/Close';
import MapPin from '@/icons/MapPin';
import Pencil from '@/icons/Pencil';
import Plus from '@/icons/Plus';
import { FamilyMemberTypeahead, type FamilyMemberOption } from '@/components/family/FamilyMemberTypeahead';
import CitationLinksField from '@/components/person/CitationLinksField';
import RecordInfoPopover from '@/components/person/RecordInfoPopover';
import ProfileSectionCount from '@/components/person/ProfileSectionCount';

import './person-life-events.css';

interface PersonLifeEventsProps {
  familyMemberId: string;
  familyId?: string;
  defaultExpanded?: boolean;
  readonly?: boolean;
  variant?: 'default' | 'profile';
  sectionMode?: 'collapsible' | 'tab-panel';
}

interface EventTypeOption {
  id: string;
  displayName: string;
  description?: string;
  multiPersonEligible: boolean;
}

const PersonLifeEvents: React.FC<PersonLifeEventsProps> = observer(
  ({
    familyMemberId,
    familyId,
    defaultExpanded = true,
    readonly = false,
    variant = 'default',
    sectionMode = 'collapsible'
  }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const isProfileVariant = variant === 'profile';
    const isTabPanel = sectionMode === 'tab-panel';
    const personLifeEventsStore = usePersonLifeEventsStore();
    const familyStore = useFamilyStore();
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [addParticipantValue, setAddParticipantValue] = useState('');
    const [pendingParticipantIds, setPendingParticipantIds] = useState<string[]>([]);
    const [eventTypes, setEventTypes] = useState<EventTypeOption[]>([]);
    const [formData, setFormData] = useState<CreateLifeEventRequest & { eventDate: string }>({
      familyMemberId,
      eventTypeId: '',
      eventDate: '',
      eventDatePrecision: 'day',
      eventPlace: '',
      detail: ''
    });

    useEffect(() => {
      personLifeEventsStore.loadLifeEventsForFamilyMember(familyMemberId);
    }, [familyMemberId, personLifeEventsStore]);

    useEffect(() => {
      if (!isAdding && !editingId) return;
      const load = async () => {
        const res = await eventTypesApi.listEventTypes(true);
        if (res.success) {
          setEventTypes(
            (res.data?.eventTypes ?? []).map(
              (et: { id: string; displayName: string; description?: string; multiPersonEligible?: boolean }) => ({
                id: et.id,
                displayName: et.displayName,
                description: et.description,
                multiPersonEligible: et.multiPersonEligible ?? false
              })
            )
          );
        }
      };
      load();
    }, [isAdding, editingId]);

    const handleAdd = () => {
      setFormData({
        familyMemberId,
        eventTypeId: '',
        eventDate: '',
        eventDatePrecision: 'day',
        eventPlace: '',
        detail: ''
      });
      setPendingParticipantIds([]);
      setIsAdding(true);
      setEditingId(null);
      personLifeEventsStore.setError(familyMemberId, null);
    };

    const handleEdit = (ev: LifeEventType) => {
      setFormData({
        familyMemberId,
        eventTypeId: ev.eventTypeId,
        eventDate: ev.eventDate ?? '',
        eventDatePrecision: (ev.eventDatePrecision as 'year' | 'month' | 'day') ?? 'day',
        eventPlace: ev.eventPlace ?? '',
        detail: ev.detail ?? ''
      });
      setEditingId(ev.id);
      setIsAdding(false);
      personLifeEventsStore.setError(familyMemberId, null);
    };

    const handleCancel = () => {
      setIsAdding(false);
      setEditingId(null);
    };

    const handleSave = async () => {
      if (!formData.eventTypeId) {
        personLifeEventsStore.setError(familyMemberId, t('errors.selectEventType'));
        return;
      }
      if (editingId) {
        const body: UpdateLifeEventRequest = {
          eventTypeId: formData.eventTypeId,
          eventDate: formData.eventDate || undefined,
          eventDatePrecision: formData.eventDatePrecision || undefined,
          eventPlace: formData.eventPlace || undefined,
          detail: formData.detail || undefined
        };
        const result = await personLifeEventsStore.updateLifeEvent(editingId, body);
        if (result.success) {
          setEditingId(null);
        } else {
          personLifeEventsStore.setError(
            familyMemberId,
            result.error?.getUserMessage?.() ?? result.error?.message ?? t('errors.updateLifeEventFailed')
          );
        }
      } else {
        const result = await personLifeEventsStore.createLifeEvent({
          ...formData,
          eventDate: formData.eventDate || undefined,
          eventPlace: formData.eventPlace || undefined,
          detail: formData.detail || undefined
        });
        if (result.success && result.data) {
          const createdEventId = result.data.id;
          let lastError: Error | null = null;
          for (const targetId of pendingParticipantIds) {
            const addResult = await personLifeEventsStore.addParticipant(createdEventId, targetId);
            if (!addResult.success) {
              lastError = addResult.error ?? new Error(t('errors.addParticipantFailed'));
            }
          }
          if (lastError) {
            const err = lastError as Error & { getUserMessage?: () => string };
            personLifeEventsStore.setError(
              familyMemberId,
              err?.getUserMessage?.() ?? err?.message ?? t('errors.lifeEventParticipantsPartial')
            );
          }
          setIsAdding(false);
        } else {
          personLifeEventsStore.setError(
            familyMemberId,
            result.error?.getUserMessage?.() ?? result.error?.message ?? t('errors.addLifeEventFailed')
          );
        }
      }
    };

    const handleDelete = async (lifeEventId: string) => {
      if (!confirm(t('confirm.deleteLifeEvent'))) return;
      const result = await personLifeEventsStore.deleteLifeEvent(lifeEventId);
      if (result.success) {
        if (editingId === lifeEventId) setEditingId(null);
      } else {
        personLifeEventsStore.setError(
          familyMemberId,
          result.error?.getUserMessage?.() ?? result.error?.message ?? t('errors.deleteLifeEventFailed')
        );
      }
    };

    const formatEventDate = (ev: LifeEventType): string => {
      if (!ev.eventDate) return '';
      const precision = (ev.eventDatePrecision as DatePrecision) ?? 'day';
      return formatTemporal(ev.eventDate, precision);
    };

    const lifeEventTypeSlug = (displayName: string): string => displayName.toLowerCase().replace(/\s+/g, '-');

    const events = personLifeEventsStore.getLifeEventsForFamilyMember(familyMemberId);
    const sortedEvents = useMemo(
      () =>
        [...events].sort((a, b) => {
          const da = a.eventDate ?? '';
          const db = b.eventDate ?? '';
          return da.localeCompare(db);
        }),
      [events]
    );
    const loading = personLifeEventsStore.isLoadingForFamilyMember(familyMemberId);
    const error = personLifeEventsStore.getErrorForFamilyMember(familyMemberId);
    const editedEvent = editingId
      ? (events.find((e) => e.id === editingId) ?? personLifeEventsStore.getLifeEventById(editingId) ?? null)
      : null;
    const editedEventType = editedEvent ? eventTypes.find((et) => et.id === editedEvent.eventTypeId) : null;
    const addingEventType = isAdding ? eventTypes.find((et) => et.id === formData.eventTypeId) : null;
    const showParticipantsSection = Boolean(
      familyId &&
        familyStore.familyId === familyId &&
        ((editingId && editedEvent && editedEventType?.multiPersonEligible) ||
          (isAdding && addingEventType?.multiPersonEligible))
    );

    const participantIdsForDisplay: string[] = isAdding
      ? [familyMemberId, ...pendingParticipantIds]
      : (editedEvent?.participantIds ?? []);

    const familyMemberOptionsForAdd: FamilyMemberOption[] = (() => {
      if (!showParticipantsSection) return [];
      const participantsSet = new Set(
        isAdding ? [familyMemberId, ...pendingParticipantIds] : (editedEvent?.participantIds ?? [])
      );
      return Array.from(familyStore.people.values())
        .filter((p) => !participantsSet.has(p.familyMemberId))
        .map((p) => ({ familyMemberId: p.familyMemberId, fullName: p.fullName }));
    })();

    const handleAddParticipant = async (targetFamilyMemberId: string) => {
      if (isAdding) {
        setPendingParticipantIds((prev) =>
          prev.includes(targetFamilyMemberId) ? prev : [...prev, targetFamilyMemberId]
        );
        setAddParticipantValue('');
        return;
      }
      if (!editingId) return;
      const result = await personLifeEventsStore.addParticipant(editingId, targetFamilyMemberId);
      if (result.success) {
        setAddParticipantValue('');
      } else {
        personLifeEventsStore.setError(
          familyMemberId,
          result.error?.getUserMessage?.() ?? result.error?.message ?? t('errors.addParticipantFailed')
        );
      }
    };

    const handleRemoveParticipant = async (targetFamilyMemberId: string) => {
      if (targetFamilyMemberId === familyMemberId) return; // Never remove current person
      if (isAdding) {
        setPendingParticipantIds((prev) => prev.filter((id) => id !== targetFamilyMemberId));
        return;
      }
      if (!editingId) return;
      const result = await personLifeEventsStore.deleteParticipant(editingId, targetFamilyMemberId);
      if (result.success) {
        setAddParticipantValue('');
      } else {
        personLifeEventsStore.setError(
          familyMemberId,
          result.error?.getUserMessage?.() ?? result.error?.message ?? t('errors.removeParticipantFailed')
        );
      }
    };

    const participantDisplayName = (pid: string): string => {
      const p = familyStore.getPerson(pid);
      return p?.fullName ?? pid;
    };

    const isCurrentPerson = (pid: string) => pid === familyMemberId;

    const addButton = !readonly ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleAdd();
        }}
        className={
          isTabPanel ? 'btn btn--ghost btn--sm person-life-events__add-button' : 'person-life-events__add-button'
        }
        disabled={isAdding || editingId !== null}
      >
        {isProfileVariant || isTabPanel ? (
          <>
            <Plus width={13} height={13} />
            {t('person.addLifeEvent')}
          </>
        ) : (
          t('person.addLifeEventPlus')
        )}
      </button>
    ) : null;

    const sectionBody = (
      <div className="person-life-events__body">
        {error && <div className="person-life-events__error">{error}</div>}

        {(isAdding || editingId) && (
          <div className="person-life-events__form">
            <h3 className="person-life-events__form-title">
              {editingId ? t('person.editLifeEventTitle') : t('person.addLifeEventTitle')}
            </h3>
            <div className="person-life-events__form-row person-life-events__form-row--event-precision-date">
              <div className="person-life-events__form-field person-life-events__form-field--event-type">
                <label className="person-life-events__form-label">
                  {t('person.eventType')} <span className="person-life-events__required">*</span>
                </label>
                <Select.Root
                  value={formData.eventTypeId || ''}
                  onValueChange={(v) => setFormData({ ...formData, eventTypeId: v })}
                >
                  <Select.Trigger className="form-select-trigger" aria-label={t('person.eventType')}>
                    <Select.Value placeholder={t('person.selectEventTypePlaceholder')} />
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                      {eventTypes.map((et) => (
                        <Select.Item key={et.id} value={et.id} className="form-select-item">
                          <Select.ItemText>{et.displayName}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>
              </div>
              <div className="person-life-events__form-field person-life-events__form-field--date">
                <DateFieldWithPrecision
                  id="life-event-date"
                  label={t('person.eventDate')}
                  value={formData.eventDate ?? ''}
                  precision={(formData.eventDatePrecision as DatePrecision) ?? 'day'}
                  onValueChange={(v) => setFormData((prev) => ({ ...prev, eventDate: v }))}
                  onPrecisionChange={(p) =>
                    setFormData((prev) => ({ ...prev, eventDatePrecision: p as DatePrecision }))
                  }
                  allowClearViaInput
                  layout="inline"
                  wrapperClassName="person-life-events__date-field-wrapper"
                />
              </div>
            </div>
            {formData.eventTypeId &&
              (() => {
                const et = eventTypes.find((e) => e.id === formData.eventTypeId);
                return et?.description ? (
                  <p className="person-life-events__form-type-description">{et.description}</p>
                ) : null;
              })()}
            <div className="person-life-events__form-field">
              <label className="person-life-events__form-label">{t('person.place')}</label>
              <input
                type="text"
                value={formData.eventPlace ?? ''}
                onChange={(e) => setFormData({ ...formData, eventPlace: e.target.value })}
                className="person-life-events__form-input"
                placeholder={t('person.placePlaceholder')}
              />
            </div>
            <div className="person-life-events__form-field">
              <label className="person-life-events__form-label">{t('person.detail')}</label>
              <input
                type="text"
                value={formData.detail ?? ''}
                onChange={(e) => setFormData({ ...formData, detail: e.target.value })}
                className="person-life-events__form-input"
                placeholder={t('person.detailPlaceholder')}
              />
            </div>
            {showParticipantsSection && (
              <div className="person-life-events__form-field person-life-events__participants">
                <label className="person-life-events__form-label">{t('person.participants')}</label>
                <ul className="person-life-events__participants-list" aria-label={t('person.participantsAria')}>
                  {participantIdsForDisplay.map((pid) => (
                    <li
                      key={pid}
                      className={`person-life-events__participants-item ${
                        isCurrentPerson(pid) ? 'person-life-events__participants-item--readonly' : ''
                      }`}
                    >
                      <span className="person-life-events__participants-name">{participantDisplayName(pid)}</span>
                      {!readonly && !isCurrentPerson(pid) && (
                        <button
                          type="button"
                          onClick={() => handleRemoveParticipant(pid)}
                          className="person-life-events__participants-remove"
                          aria-label={t('person.removeFromEventAria', { name: participantDisplayName(pid) })}
                        >
                          {tCommon('remove')}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                {!readonly && familyMemberOptionsForAdd.length > 0 && (
                  <div className="person-life-events__participants-add">
                    <FamilyMemberTypeahead
                      key={participantIdsForDisplay.length}
                      value={addParticipantValue}
                      onChange={(id) => {
                        handleAddParticipant(id);
                        setAddParticipantValue('');
                      }}
                      members={familyMemberOptionsForAdd}
                      placeholder={t('person.addParticipantPlaceholder')}
                      label={t('person.addParticipant')}
                      maxResults={8}
                    />
                  </div>
                )}
              </div>
            )}
            {familyId && editingId && (
              <CitationLinksField
                familyId={familyId}
                targetType="life_event"
                targetId={editingId}
                readonly={readonly}
              />
            )}
            <div className="person-life-events__form-actions">
              <button type="button" onClick={handleCancel} className="person-life-events__cancel-button">
                {tCommon('cancel')}
              </button>
              <button type="button" onClick={handleSave} className="person-life-events__save-button">
                {editingId ? t('actions.save') : tCommon('add')}
              </button>
            </div>
          </div>
        )}

        {loading && <div className="person-life-events__loading">{t('person.loadingLifeEvents')}</div>}
        {!loading && !error && !isAdding && !editingId && events.length === 0 && (
          <div className="person-life-events__empty">
            {readonly ? t('person.emptyLifeEventsReadonly') : t('person.emptyLifeEvents')}
          </div>
        )}
        {!loading && !error && events.length > 0 && isProfileVariant && (
          <div className="person-life-events__timeline">
            {sortedEvents.map((ev) =>
              editingId === ev.id ? null : (
                <div
                  key={ev.id}
                  className={`person-life-events__timeline-item person-life-events__timeline-item--${lifeEventTypeSlug(ev.eventTypeDisplayName)}`}
                >
                  <div className="person-life-events__timeline-dot" aria-hidden />
                  <div className="person-life-events__timeline-head">
                    <div className="person-life-events__timeline-main">
                      {ev.eventDate && <div className="person-life-events__timeline-year">{formatEventDate(ev)}</div>}
                      <div>
                        <span className="person-life-events__timeline-type">{ev.eventTypeDisplayName}</span>
                        {ev.detail && <span className="person-life-events__timeline-detail">{ev.detail}</span>}
                      </div>
                      {ev.eventPlace && (
                        <div className="person-life-events__timeline-place">
                          <MapPin width={11} height={11} />
                          {ev.eventPlace}
                        </div>
                      )}
                    </div>
                    {familyId && (
                      <div className="person-life-events__timeline-meta">
                        <div className="profile-entry-actions">
                          <RecordInfoPopover
                            createdAt={ev.createdAt}
                            updatedAt={ev.updatedAt}
                            familyId={familyId}
                            targetType="life_event"
                            targetId={ev.id}
                            detail={ev.detail}
                            description={ev.description}
                            sourceNotes={ev.sourceNotes}
                            createdByNickname={ev.createdByNickname}
                            updatedByNickname={ev.updatedByNickname}
                            triggerClassName="profile-entry-icon-btn record-info-popover__trigger"
                            ariaLabel={t('person.recordInfo')}
                          />
                          {!readonly && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleEdit(ev)}
                                className="profile-entry-icon-btn"
                                disabled={isAdding || editingId !== null}
                                aria-label={t('person.editEventAria', { name: ev.eventTypeDisplayName })}
                                title={t('person.editLifeEvent')}
                              >
                                <Pencil width={13} height={13} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(ev.id)}
                                className="profile-entry-icon-btn profile-entry-icon-btn--danger"
                                disabled={isAdding || editingId !== null}
                                aria-label={t('person.deleteEventAria', { name: ev.eventTypeDisplayName })}
                                title={t('person.deleteLifeEvent')}
                              >
                                <Close width={13} height={13} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            )}
          </div>
        )}
        {!loading && !error && events.length > 0 && !isProfileVariant && (
          <div className="person-life-events__list">
            {events.map((ev) => (
              <div key={ev.id} className="person-life-events__item">
                {editingId === ev.id ? null : (
                  <>
                    <div className="person-life-events__item-row">
                      <div className="person-life-events__item-main">
                        <span className="person-life-events__item-type">{ev.eventTypeDisplayName}</span>
                        {ev.eventDate && (
                          <>
                            <span className="person-life-events__item-separator">{t('person.eventOn')}</span>
                            <span className="person-life-events__item-date-inline">{formatEventDate(ev)}</span>
                          </>
                        )}
                        {ev.eventPlace && (
                          <>
                            <span className="person-life-events__item-separator">{t('person.eventAt')}</span>
                            <span className="person-life-events__item-place-inline">{ev.eventPlace}</span>
                          </>
                        )}
                        {familyId && (
                          <RecordInfoPopover
                            createdAt={ev.createdAt}
                            updatedAt={ev.updatedAt}
                            familyId={familyId}
                            targetType="life_event"
                            targetId={ev.id}
                            detail={ev.detail}
                            description={ev.description}
                            sourceNotes={ev.sourceNotes}
                            createdByNickname={ev.createdByNickname}
                            updatedByNickname={ev.updatedByNickname}
                            triggerClassName="person-life-events__record-info-trigger record-info-popover__trigger"
                            ariaLabel={t('person.recordInfo')}
                          />
                        )}
                      </div>
                      {!readonly && (
                        <div className="person-life-events__item-actions">
                          <button
                            type="button"
                            onClick={() => handleEdit(ev)}
                            className="person-life-events__edit-button"
                            disabled={isAdding || editingId !== null}
                          >
                            {tCommon('edit')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(ev.id)}
                            className="person-life-events__delete-button"
                            disabled={isAdding || editingId !== null}
                          >
                            {tCommon('delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );

    if (isTabPanel) {
      return (
        <div className="person-life-events person-life-events--profile person-life-events--tab-panel">
          <div className="tab-panel-card__head">
            <h2 className="tab-panel-card__title">{t('person.tabs.lifeEvents')}</h2>
            {addButton}
          </div>
          <div className="tab-panel-card__body">{sectionBody}</div>
        </div>
      );
    }

    return (
      <Collapsible.Root
        open={expanded}
        onOpenChange={setExpanded}
        className={isProfileVariant ? 'person-life-events person-life-events--profile' : 'person-life-events'}
      >
        <div className="person-life-events__header">
          <Collapsible.Trigger asChild>
            <button type="button" className="person-life-events__header-button">
              <CollapsibleChevron className="person-life-events__chevron" />
              <h2 className="person-life-events__title">
                {t('person.tabs.lifeEvents')}
                {isProfileVariant && <ProfileSectionCount count={events.length} />}
              </h2>
            </button>
          </Collapsible.Trigger>
          {addButton}
        </div>

        <Collapsible.Content>{sectionBody}</Collapsible.Content>
      </Collapsible.Root>
    );
  }
);

export default PersonLifeEvents;
